import type { DialogueLine, LibraryKind, SceneView, ShotView } from "@dramapie/shared";
import { useCallback, useEffect, useState, type CSSProperties } from "react";

import { formatDuration, formatPoints } from "../lib/format";
import { MEDIA_SLOT_LABEL } from "../lib/generations";
import { DIALOGUE_TYPE_LABEL, SHOT_STATUS_META, shortId } from "../lib/status";
import { MediaLightbox, MediaTile, type PreviewTarget } from "./MediaPreview";
import { useImageViewer } from "./media/ImageViewer";

/** 镜头卡 chips → 资料库定位（契约 M5 §1）。 */
export interface LibraryChipTarget {
  kind: LibraryKind;
  id?: string;
  name?: string;
}

export interface ShotCardEditing {
  disabled?: boolean;
  selected: boolean;
  onToggleSelect: (shotId: string) => void;
  onDurationCommit: (shotId: string, duration: number) => void;
  onDialogueCommit: (shotId: string, lines: DialogueLine[]) => void;
  /** 确认本镜（写回 status=approved）。 */
  onConfirm?: (shotId: string) => void;
  /** 重抽本镜首帧（走预估 → 确认层，不直接扣点）。 */
  onRegenerate?: (shotId: string) => void;
  /** 点角色 / 场景 chip：打开资料库并选中对应条目。 */
  onFocusLibrary?: (target: LibraryChipTarget) => void;
  /** 当前镜（画布 / 时间线 / 镜头带选中）：卡片高亮，与多选分开。 */
  activeShotId?: string | null;
  /** 切到画布并定位本镜（M6）。 */
  onOpenCanvas?: (shotId: string) => void;
  /** 打开「首尾帧 · 确认」弹层（Wiki F-01~F-07）：叠加 / 像素差异 / checks。 */
  onOpenFrames?: (shotId: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

interface ShotCardProps {
  shot: ShotView;
  /** 所属场次，用于场景 chip 与检查列。 */
  scene?: SceneView | null;
  editing?: ShotCardEditing;
  /** 时间轴上的入场错峰序号。 */
  index?: number;
}

function CheckList({ shot }: { shot: ShotView }) {
  const passCount = shot.checks.filter((check) => check.pass).length;
  const allPass = passCount === shot.checks.length;
  return (
    <div className="dp-shot-checks">
      <p className={`dp-checks-head${allPass ? "" : " is-bad"}`}>
        CHECKS <b>{`${passCount}/${shot.checks.length}`}</b>
      </p>
      {shot.checks.map((check, index) => (
        <p key={`${shot.id}-check-${index}`} className={`dp-check${check.pass ? "" : " is-fail"}`}>
          <span className={`mark ${check.pass ? "ok" : "bad"}`} aria-hidden="true">
            {check.pass ? "✓" : "✗"}
          </span>
          <span className="dp-sr">{check.pass ? "通过" : "未过"}</span>
          <span>{check.q}</span>
        </p>
      ))}
    </div>
  );
}

/**
 * 镜头卡：`92px 缩略 | 主体 | 206px 检查列`。
 *
 * - 没有 check 时整列隐藏，卡片退化成两栏（规范 §4 的优雅降级）。
 * - 没有角色 / 场景就隐藏 chips；没有台词显示一行占位，不撑高卡片。
 * - 确认 = `update_shot status=approved`；重抽 = 该镜首帧的预估流程；在画布看 = 切到画布并定位。
 */
export function ShotCard({ shot, scene, editing, index = 0 }: ShotCardProps) {
  const [durationDraft, setDurationDraft] = useState(String(shot.duration));
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const closePreview = useCallback(() => setPreview(null), []);
  const imageViewer = useImageViewer();
  const [dialogueDrafts, setDialogueDrafts] = useState<string[]>(
    shot.dialogue.map((line) => line.text),
  );

  // 外部（命令成功 / 409 覆盖 / WS 事件）改动后，草稿跟着回到真源。
  useEffect(() => {
    setDurationDraft(String(shot.duration));
  }, [shot.duration]);

  useEffect(() => {
    setDialogueDrafts(shot.dialogue.map((line) => line.text));
  }, [shot.dialogue]);

  // 点缩略图：图走全屏看图（同镜的首帧 / 尾帧可 ← → 切换），视频仍用原来的播放浮层。
  function openMedia(target: PreviewTarget) {
    if (target.media.kind === "video") {
      setPreview(target);
      return;
    }
    const images = [
      shot.keyframe?.url ? { slot: "keyframe" as const, url: shot.keyframe.url } : null,
      shot.endFrame?.url ? { slot: "endframe" as const, url: shot.endFrame.url } : null,
    ].filter((item): item is { slot: "keyframe" | "endframe"; url: string } => item !== null);
    const index = Math.max(
      0,
      images.findIndex((item) => item.slot === target.slot),
    );
    imageViewer.open(
      images.map((item) => ({
        src: item.url,
        title: `第 ${shot.order} 镜${MEDIA_SLOT_LABEL[item.slot]}`,
        subtitle: MEDIA_SLOT_LABEL[item.slot],
      })),
      index,
    );
  }

  const meta = SHOT_STATUS_META[shot.status];
  const tone = meta.tone;
  const disabled = editing ? editing.disabled === true : true;
  const hasChecks = shot.checks.length > 0;
  const code = shortId(shot.id);
  const costLabel =
    shot.costPoints > 0 ? `${formatPoints(shot.costPoints)} 点 · v${shot.rev}` : `— · v${shot.rev}`;

  function commitDuration() {
    if (!editing || editing.disabled) return;
    const value = Number(durationDraft);
    if (!Number.isFinite(value) || value === shot.duration) {
      setDurationDraft(String(shot.duration));
      return;
    }
    // 非法值也交给上层 toast，卡片自身回退到真源。
    setDurationDraft(String(shot.duration));
    editing.onDurationCommit(shot.id, value);
  }

  function setDialogueDraft(lineIndex: number, value: string) {
    setDialogueDrafts((prev) => {
      const next = [...prev];
      next[lineIndex] = value;
      return next;
    });
  }

  function commitDialogue(lineIndex: number) {
    if (!editing || editing.disabled) return;
    const current = shot.dialogue.map((line) => line.text);
    const draft = dialogueDrafts[lineIndex] ?? current[lineIndex] ?? "";
    if (draft === current[lineIndex]) return;
    const lines = shot.dialogue.map((line, index2) => ({
      ...line,
      text: index2 === lineIndex ? draft : line.text,
    }));
    editing.onDialogueCommit(shot.id, lines);
  }

  return (
    <article
      aria-label={`镜头 ${shot.order}`}
      data-shot-id={shot.id}
      data-no={code}
      data-active={
        editing?.selected === true || editing?.activeShotId === shot.id ? "true" : undefined
      }
      className={`dp-shot-card is-${tone}`}
      style={{ "--i": index } as CSSProperties}
    >
      <div className="dp-shot-thumb">
        <MediaTile
          shotId={shot.id}
          slot="keyframe"
          media={shot.keyframe}
          shotOrder={shot.order}
          onOpen={openMedia}
        />
        <span className={`dp-thumb-status is-${tone}`}>{meta.label}</span>
        <span className="dp-thumb-dur">{formatDuration(shot.duration)}</span>
        {shot.endFrame || shot.clip ? (
          <div className="dp-shot-extra">
            {shot.endFrame ? (
              <MediaTile
                shotId={shot.id}
                slot="endframe"
                media={shot.endFrame}
                shotOrder={shot.order}
                onOpen={openMedia}
              />
            ) : null}
            {shot.clip ? (
              <MediaTile
                shotId={shot.id}
                slot="clip"
                media={shot.clip}
                shotOrder={shot.order}
                duration={shot.duration}
                onOpen={openMedia}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="dp-shot-body">
        <div className="dp-shot-top">
          <span className="dp-shot-id">{code}</span>
          {shot.framing ? <span className="dp-shot-tag">{shot.framing}</span> : null}
          <span className={`dp-shot-status is-${tone}`}>
            <i aria-hidden="true" />
            {meta.label}
          </span>
          <span className="dp-shot-spacer" />
          <span className="dp-shot-cost">{costLabel}</span>
        </div>

        {shot.action ? <p className="dp-shot-action">{shot.action}</p> : null}

        <div className="dp-shot-dialogue">
          {shot.dialogue.length === 0 ? (
            <span className="dp-dline-empty">无台词</span>
          ) : editing ? (
            shot.dialogue.map((line, lineIndex) => (
              <span key={`${shot.id}-line-${lineIndex}`} className="dp-dline">
                <b>{line.who}</b>
                <span className="dp-dline-type">{DIALOGUE_TYPE_LABEL[line.type]}</span>
                <input
                  aria-label={`镜头 ${shot.order} 台词 ${lineIndex + 1}`}
                  value={dialogueDrafts[lineIndex] ?? line.text}
                  disabled={disabled}
                  maxLength={120}
                  onChange={(event) => setDialogueDraft(lineIndex, event.target.value)}
                  onBlur={() => commitDialogue(lineIndex)}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    commitDialogue(lineIndex);
                  }}
                  className="dp-dline-input"
                />
              </span>
            ))
          ) : (
            shot.dialogue.map((line, lineIndex) => (
              <span key={`${shot.id}-line-${lineIndex}`} className="dp-dline">
                <b>{line.who}</b>
                <span>「{line.text}」</span>
              </span>
            ))
          )}
        </div>

        {shot.chars.length > 0 || scene ? (
          <div className="dp-shot-chips">
            {shot.chars.map((char) =>
              editing?.onFocusLibrary ? (
                <button
                  key={`${shot.id}-${char}`}
                  type="button"
                  className="dp-chip char is-link"
                  title={`在资料库查看 ${shortId(char)}`}
                  onClick={() => editing.onFocusLibrary?.({ kind: "character", id: char })}
                >
                  {shortId(char)}
                </button>
              ) : (
                <span key={`${shot.id}-${char}`} className="dp-chip char">
                  {shortId(char)}
                </span>
              ),
            )}
            {scene ? (
              editing?.onFocusLibrary ? (
                <button
                  type="button"
                  className="dp-chip scene is-link"
                  title={`在资料库查看场景 ${scene.name}`}
                  onClick={() => editing.onFocusLibrary?.({ kind: "location", name: scene.name })}
                >
                  {scene.name}
                </button>
              ) : (
                <span className="dp-chip scene">{scene.name}</span>
              )
            ) : null}
          </div>
        ) : null}

        <div className="dp-shot-foot">
          <span className="dp-shot-time">
            <input
              aria-label={`镜头 ${shot.order} 时长`}
              type="number"
              min={0.5}
              max={15}
              step={0.1}
              value={durationDraft}
              disabled={disabled}
              onChange={(event) => setDurationDraft(event.target.value)}
              onBlur={commitDuration}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                commitDuration();
              }}
            />
            <span aria-hidden="true">s</span>
          </span>

          {editing ? (
            <label className="dp-select">
              <input
                aria-label={`选择镜头 ${shot.order}`}
                type="checkbox"
                checked={editing.selected}
                onChange={() => editing.onToggleSelect(shot.id)}
              />
              <span aria-hidden="true">选</span>
            </label>
          ) : null}

          <span className="dp-shot-spacer" />

          <button
            type="button"
            className="dp-btn sm"
            disabled={!editing || disabled || shot.status === "approved"}
            onClick={() => editing?.onConfirm?.(shot.id)}
          >
            确认
          </button>
          <button
            type="button"
            className="dp-btn sm"
            disabled={!editing?.onOpenFrames || disabled}
            title={editing?.onOpenFrames ? "看首尾帧、叠加与像素差异" : "还没有帧"}
            onClick={() => editing?.onOpenFrames?.(shot.id)}
          >
            看帧
          </button>
          <button
            type="button"
            className="dp-btn sm"
            disabled={!editing || disabled}
            onClick={() => editing?.onRegenerate?.(shot.id)}
          >
            重抽
          </button>
          <button
            type="button"
            className="dp-btn sm"
            disabled={!editing?.onOpenCanvas || disabled}
            title={editing?.onOpenCanvas ? "切到画布并定位这一镜" : "画布还没准备好"}
            onClick={() => editing?.onOpenCanvas?.(shot.id)}
          >
            在画布看
          </button>

          {editing && (editing.onMoveUp || editing.onMoveDown) ? (
            <>
              <button
                type="button"
                aria-label={`镜头 ${shot.order} 前移`}
                disabled={!editing.onMoveUp || disabled}
                onClick={editing.onMoveUp}
                className="dp-btn sm ghost"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`镜头 ${shot.order} 后移`}
                disabled={!editing.onMoveDown || disabled}
                onClick={editing.onMoveDown}
                className="dp-btn sm ghost"
              >
                ↓
              </button>
            </>
          ) : null}
        </div>
      </div>

      {hasChecks ? <CheckList shot={shot} /> : null}

      <MediaLightbox target={preview} onClose={closePreview} />
    </article>
  );
}
